/**
 * Polygon geometry helpers for the free-polygon Zone Editor (Phase 5C).
 *
 * All points use the normalized {x, y} ∈ [0,1] coordinates the editor
 * already stores in `zone.polygon_points`. The actual screen / media
 * pixel mapping is the caller's responsibility — we operate purely on
 * unit-space to keep the math zoom-independent.
 */

/** True iff segments AB and CD intersect strictly (no shared endpoint). */
function segmentsIntersect(a, b, c, d) {
  const ccw = (p, q, r) => (r.y - p.y) * (q.x - p.x) - (q.y - p.y) * (r.x - p.x);
  const d1 = ccw(c, d, a);
  const d2 = ccw(c, d, b);
  const d3 = ccw(a, b, c);
  const d4 = ccw(a, b, d);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  return false; // collinear/touching is fine — adjacent edges share endpoints
}

/**
 * polygonSelfIntersects(points)
 * Returns true if any two non-adjacent edges of the closed polygon cross.
 * The closing edge (last → first) is included. Adjacent edges sharing a
 * vertex are skipped to avoid false positives at the shared corner.
 */
export function polygonSelfIntersects(points) {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Skip adjacent edges & the wrap-around adjacency
      if (j === i || j === (i + 1) % n) continue;
      if ((j + 1) % n === i) continue;
      const c = points[j];
      const d = points[(j + 1) % n];
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

/** Snap candidate to nearest multiple of `step` in *unit-space*. */
export function snapToGrid({ x, y }, gridSize, mediaSize) {
  const stepX = gridSize / mediaSize.w;
  const stepY = gridSize / mediaSize.h;
  return {
    x: Math.round(x / stepX) * stepX,
    y: Math.round(y / stepY) * stepY,
  };
}

/**
 * Find an alignment with any other vertex's X or Y. Returns
 * {x, y, guides:[{type:'v'|'h', value}]} — `value` is in unit-space.
 * If nothing aligns within `tolUnit` for an axis, that axis stays raw.
 */
export function alignToVertices(raw, others, tolUnit) {
  let bestVx = null, bestVxDist = Infinity;
  let bestHy = null, bestHyDist = Infinity;
  for (const p of others) {
    const dx = Math.abs(p.x - raw.x);
    if (dx < tolUnit && dx < bestVxDist) { bestVx = p.x; bestVxDist = dx; }
    const dy = Math.abs(p.y - raw.y);
    if (dy < tolUnit && dy < bestHyDist) { bestHy = p.y; bestHyDist = dy; }
  }
  const out = { x: bestVx ?? raw.x, y: bestHy ?? raw.y, guides: [] };
  if (bestVx != null) out.guides.push({ type: "v", value: bestVx });
  if (bestHy != null) out.guides.push({ type: "h", value: bestHy });
  return out;
}

/** Align: returns a transformed list of `points` with the chosen vertices'
 *  X or Y replaced by the leader's value. `mode` ∈ left|right|top|bottom|cx|cy. */
export function alignVertices(points, indices, mode) {
  if (indices.length < 2) return points;
  const xs = indices.map((i) => points[i].x);
  const ys = indices.map((i) => points[i].y);
  let targetX = null, targetY = null;
  switch (mode) {
    case "left":   targetX = Math.min(...xs); break;
    case "right":  targetX = Math.max(...xs); break;
    case "cx":     targetX = xs.reduce((a, b) => a + b, 0) / xs.length; break;
    case "top":    targetY = Math.min(...ys); break;
    case "bottom": targetY = Math.max(...ys); break;
    case "cy":     targetY = ys.reduce((a, b) => a + b, 0) / ys.length; break;
    default: return points;
  }
  const set = new Set(indices);
  return points.map((p, i) => {
    if (!set.has(i)) return p;
    return { x: targetX != null ? targetX : p.x, y: targetY != null ? targetY : p.y };
  });
}

/** Distribute selected vertices evenly between the min and max of the chosen
 *  axis. Needs ≥3 selected to make sense. */
export function distributeVertices(points, indices, axis /* 'x'|'y' */) {
  if (indices.length < 3) return points;
  const sorted = indices.slice().sort((a, b) => points[a][axis] - points[b][axis]);
  const first = points[sorted[0]][axis];
  const last  = points[sorted[sorted.length - 1]][axis];
  const span = last - first;
  const step = span / (sorted.length - 1);
  const out = points.slice();
  sorted.forEach((idx, k) => {
    out[idx] = { ...out[idx], [axis]: first + step * k };
  });
  return out;
}
